import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Save, Package } from "lucide-react";
import { fmtINR, today } from "@/lib/format";

/**
 * BOM Assembly Receive — Iter-96.
 * Consumes an open BOM issue and posts DR-FG · CR-WIP.
 */
export default function BOMReceive() {
  const [openIssues, setOpenIssues] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [view, setView] = useState(null);
  const [form, setForm] = useState({ issue_id: "", fg_qty: 0, unit_cost: "", fg_warehouse_id: "", date: today() });

  const load = async () => {
    const [issues, wh, recv] = await Promise.all([
      api.get("/ops/bom/issues").then((r) => r.data).catch(() => []),
      api.get("/warehouses").then((r) => r.data).catch(() => []),
      api.get("/ops/bom/receipts").then((r) => r.data).catch(() => []),
    ]);
    setOpenIssues((issues || []).filter((i) => i.status !== "closed"));
    setWarehouses(wh || []);
    setReceipts(recv || []);
  };
  useEffect(() => { load(); }, []);

  const activeIssue = useMemo(() => openIssues.find((i) => i.id === form.issue_id), [form.issue_id, openIssues]);
  const autoUnitCost = useMemo(() => {
    if (!activeIssue || !form.fg_qty) return 0;
    return Math.round((Number(activeIssue.total_value || 0) / Number(form.fg_qty || 1)) * 100) / 100;
  }, [activeIssue, form.fg_qty]);
  const effectiveUC = Number(form.unit_cost) > 0 ? Number(form.unit_cost) : autoUnitCost;
  const fgValue = (Number(form.fg_qty) || 0) * effectiveUC;

  const submit = async () => {
    if (!form.issue_id) return toast.error("Select an open issue");
    if (!form.fg_qty || form.fg_qty <= 0) return toast.error("Enter actual FG qty");
    try {
      const body = {
        issue_id: form.issue_id, fg_item_id: activeIssue.fg_item_id, fg_qty: Number(form.fg_qty),
        fg_warehouse_id: form.fg_warehouse_id || null,
        unit_cost: form.unit_cost === "" ? null : Number(form.unit_cost),
        date: form.date,
      };
      const r = await api.post("/ops/bom/receive", body);
      toast.success(`Receipt ${r.data.receipt.receipt_number} · FG DR ₹${(r.data.receipt.fg_value || 0).toFixed(0)}`);
      setForm({ issue_id: "", fg_qty: 0, unit_cost: "", fg_warehouse_id: "", date: today() });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Receipt failed"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="bom-receive-page">
      <PageHeader eyebrow="Manufacturing · Iter-96" title="BOM Assembly Receive"
        description="Consume an open BOM issue with actual FG qty. Posts DR-FG · CR-WIP using auto or overridden unit cost." />

      <Card className="rounded-sm p-4 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <Label>Open issue</Label>
            <Select value={form.issue_id} onValueChange={(v) => setForm({ ...form, issue_id: v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="br-issue"><SelectValue placeholder="Pick a pending issue" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {openIssues.length === 0 && <div className="p-3 text-xs text-muted-foreground">No open issues · post an Issue first</div>}
                {openIssues.map((i) => <SelectItem key={i.id} value={i.id}>{i.issue_number} · WIP ₹{(i.total_value || 0).toFixed(0)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Actual FG qty</Label><Input type="number" value={form.fg_qty} onChange={(e) => setForm({ ...form, fg_qty: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums" data-testid="br-fg-qty" /></div>
          <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm mt-1" /></div>
          <div>
            <Label>FG warehouse</Label>
            <Select value={form.fg_warehouse_id} onValueChange={(v) => setForm({ ...form, fg_warehouse_id: v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="br-warehouse"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Unit cost override</Label>
            <Input type="number" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} className="rounded-sm mt-1 tabular-nums" placeholder={`Auto ₹${autoUnitCost.toFixed(2)}`} data-testid="br-unit-cost" />
            <div className="text-[10px] text-muted-foreground mt-0.5">Leave blank to auto-compute as issue value / FG qty</div>
          </div>
        </div>
      </Card>

      <Card className="rounded-sm p-4 bg-accent/5 border-accent/30 flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase text-muted-foreground">// FG value to be booked</div>
          <div className="text-2xl font-bold tabular-nums mt-1">{fmtINR(fgValue)}</div>
          <div className="text-xs text-muted-foreground">{form.fg_qty || 0} × ₹{effectiveUC.toFixed(2)} · DR 1330 FG · CR 1320 WIP</div>
        </div>
        <Button onClick={submit} className="rounded-sm gap-1 bg-accent hover:bg-accent/90 text-white" data-testid="br-submit">
          <Save className="w-4 h-4" /> Post receipt voucher
        </Button>
      </Card>

      <Card className="rounded-sm p-4">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">// Recent receipts</div>
        <table className="w-full text-sm">
          <thead><tr className="text-[10px] font-mono uppercase text-muted-foreground bg-muted/40">
            <th className="p-2 text-left">Receipt #</th><th className="p-2 text-left">Date</th><th className="p-2 text-right">FG Qty</th><th className="p-2 text-right">Unit ₹</th><th className="p-2 text-right">FG Value ₹</th>
          </tr></thead>
          <tbody>
            {receipts.length === 0 && <tr><td colSpan="5" className="p-4 text-center text-muted-foreground">No receipts yet</td></tr>}
            {receipts.slice(0, 10).map((r) => (
              <tr key={r.id} className="border-t cursor-pointer hover:bg-muted/40" onClick={() => setView(r)} data-testid={`br-recv-${r.receipt_number}`}>
                <td className="p-2 font-mono text-xs">{r.receipt_number}</td>
                <td className="p-2 text-xs">{r.date}</td>
                <td className="p-2 text-right tabular-nums">{r.fg_qty}</td>
                <td className="p-2 text-right tabular-nums">{fmtINR(r.unit_cost)}</td>
                <td className="p-2 text-right tabular-nums font-medium">{fmtINR(r.fg_value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Sheet open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <SheetContent side="right" className="w-full sm:max-w-xl rounded-none space-y-4 overflow-y-auto" data-testid="br-view-drawer">
          <SheetHeader className="pb-3 border-b">
            <SheetTitle className="font-heading flex items-center gap-3">
              <Package className="w-5 h-5 text-accent" />
              BOM Receipt {view?.receipt_number}
            </SheetTitle>
          </SheetHeader>
          {view && (
            <div className="text-sm space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Date</div><div className="mt-1 font-mono">{view.date}</div></div>
                <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">FG Qty</div><div className="mt-1 text-lg tabular-nums font-bold">{view.fg_qty}</div></div>
                <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Unit cost</div><div className="mt-1 tabular-nums">{fmtINR(view.unit_cost || 0)}</div></div>
              </div>
              <div className="card-sharp p-4 bg-accent/5 border-accent/30">
                <div className="text-[10px] font-mono uppercase text-accent">// FG value posted</div>
                <div className="mt-1 text-2xl font-bold tabular-nums">{fmtINR(view.fg_value || 0)}</div>
                <div className="text-xs text-muted-foreground mt-1">DR 1330 FG · CR 1320 WIP</div>
              </div>
              {view.issue_id && <div className="border rounded-sm p-3 bg-muted/20"><div className="text-[10px] font-mono uppercase text-muted-foreground">// Source BOM Issue</div><div className="mt-1 font-mono text-xs">{view.issue_id}</div></div>}
              {view.voucher_id && <div className="border rounded-sm p-3 bg-accent/5"><div className="text-[10px] font-mono uppercase text-accent">// Accounting voucher</div><div className="mt-1 font-mono text-xs">{view.voucher_id}</div></div>}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
